/**
 * Pure-function annotation pre-processing for the Markdown component.
 *
 * Extracted from `Markdown.tsx` so unit tests can import without pulling in
 * `react-markdown` (which is published as ESM-only and Jest can't transform
 * without extra config).
 */

/** Returns true if the character at `index` in `text` is inside a [[...]] citation marker. */
function isInsideCitationLink(text: string, index: number): boolean {
  const openPos = text.lastIndexOf("[[", index);
  if (openPos === -1) return false;
  const closePos = text.indexOf("]]", openPos + 2);
  return closePos === -1 || closePos >= index;
}

/**
 * Find the last occurrence of `pattern` in `text` that is NOT inside a [[...]] citation link.
 * Searching backwards avoids matching annotation text that appears inside citation markers
 * (e.g. "31" inside "[[warehouse:2/31329:NAME]]"), which would break the citation.
 */
function findLastIndexOutsideCitations(text: string, pattern: string): number {
  let pos = text.length - 1;
  while (pos >= 0) {
    const idx = text.lastIndexOf(pattern, pos);
    if (idx === -1) return -1;
    if (!isInsideCitationLink(text, idx)) return idx;
    pos = idx - 1;
  }
  return -1;
}

/**
 * Process annotation codeblocks in the markdown content.
 *
 * For ai_N annotations: find the annotation's data.text in the content immediately
 * before the codeblock (searching for bold "**text**", italic "*text*", or plain text)
 * and replace that occurrence with an annotation-trigger span so the text itself
 * becomes the clickable popover trigger — no duplicate badge rendered separately.
 *
 * For table_N / chart_N: keep as fenced code block with a blank line before it so
 * the pre handler renders a ProvenanceBadge block element.
 */
export function processAnnotations(content: string): string {
  const annotationRegex = /\n*```annotation\n([\s\S]*?)\n```/g;
  let result = "";
  let lastIndex = 0;
  let match;

  while ((match = annotationRegex.exec(content)) !== null) {
    const json = match[1];
    const matchStart = match.index;
    const matchEnd = match.index + match[0].length;

    try {
      const parsed = JSON.parse(json.trim());
      const label: string = parsed.label ?? "";

      if (label.startsWith("table_") || label.startsWith("chart_")) {
        result += content.slice(lastIndex, matchStart) + `\n\n\`\`\`annotation\n${json}\n\`\`\``;
        lastIndex = matchEnd;
        continue;
      }

      const data = parsed.data ?? {};
      const annotationText: string = data.text ?? "";
      const escapedJson = json.trim().replace(/&/g, "&amp;").replace(/"/g, "&quot;");
      const beforeMatch = content.slice(lastIndex, matchStart);

      // If the annotation block itself is inside an unclosed [[...]] citation marker,
      // skip it entirely so the citation text rejoins seamlessly. Inserting any span
      // here would break the citation and cause processCitations to double-escape the
      // span's data-annotation attribute as visible label text.
      // e.g. "[[warehouse:2/31\n```annotation...```\n329:WH]]" → "[[warehouse:2/31329:WH]]"
      const prefixUpToMatch = content.slice(0, matchStart);
      if (
        prefixUpToMatch.length > 0 &&
        isInsideCitationLink(prefixUpToMatch, prefixUpToMatch.length - 1)
      ) {
        result += beforeMatch;
        // Consume the trailing \n (if any) to close the gap between the split parts.
        lastIndex = matchEnd + (matchEnd < content.length && content[matchEnd] === "\n" ? 1 : 0);
        continue;
      }

      if (annotationText) {
        // Try bold (**text**), then italic (*text*), then plain text — last occurrence wins.
        // Skip matches inside [[...]] citation markers to avoid breaking citation links.
        const boldPattern = `**${annotationText}**`;
        const italicPattern = `*${annotationText}*`;
        const boldIdx = findLastIndexOutsideCitations(beforeMatch, boldPattern);
        const italicIdx =
          boldIdx === -1 ? findLastIndexOutsideCitations(beforeMatch, italicPattern) : -1;
        const plainIdx =
          boldIdx === -1 && italicIdx === -1
            ? findLastIndexOutsideCitations(beforeMatch, annotationText)
            : -1;

        if (boldIdx !== -1) {
          result +=
            beforeMatch.slice(0, boldIdx) +
            `<span class="annotation-trigger" data-annotation="${escapedJson}"><strong>${annotationText}</strong></span>` +
            beforeMatch.slice(boldIdx + boldPattern.length);
        } else if (italicIdx !== -1) {
          result +=
            beforeMatch.slice(0, italicIdx) +
            `<span class="annotation-trigger" data-annotation="${escapedJson}"><em>${annotationText}</em></span>` +
            beforeMatch.slice(italicIdx + italicPattern.length);
        } else if (plainIdx !== -1) {
          result +=
            beforeMatch.slice(0, plainIdx) +
            `<span class="annotation-trigger" data-annotation="${escapedJson}">${annotationText}</span>` +
            beforeMatch.slice(plainIdx + annotationText.length);
        } else {
          result +=
            beforeMatch +
            `<span class="annotation-inline" data-annotation="${escapedJson}"></span>`;
        }
      } else {
        result +=
          beforeMatch + `<span class="annotation-inline" data-annotation="${escapedJson}"></span>`;
      }

      // Consume the trailing \n after the annotation codeblock so continuation
      // text stays inline. Only skip consumption if the next line starts a
      // block-level element (#, >, -, *, +, ordered list digit).
      if (
        matchEnd < content.length &&
        content[matchEnd] === "\n" &&
        content[matchEnd + 1] !== "\n"
      ) {
        const nextChar = content[matchEnd + 1];
        const isBlockStart =
          nextChar === "#" ||
          nextChar === ">" ||
          nextChar === "-" ||
          nextChar === "*" ||
          nextChar === "+" ||
          (nextChar >= "0" && nextChar <= "9");
        lastIndex = isBlockStart ? matchEnd : matchEnd + 1;
      } else {
        lastIndex = matchEnd;
      }
    } catch {
      result += content.slice(lastIndex, matchEnd);
      lastIndex = matchEnd;
    }
  }

  result += content.slice(lastIndex);
  return result;
}
